import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';

import { Link } from 'react-router-dom';
import Button from 'react-bootstrap/lib/Button';
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import Tooltip from 'react-bootstrap/lib/Tooltip';

import {
  aOrAn,
  capitalizeWords,
  formatLocalDate,
  hasAvatar,
} from 'helpers/HelperFunctions';

import Markdown from 'components/Shared/Markdown';
import Panel from 'components/Panel/Panel';
import Price from 'components/Price/Price';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import Modal from 'components/Modal/Modal';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import UserStatusBadges from 'components/Badges/UserStatusBadges/UserStatusBadges';
import ExternalClient from 'components/Badges/ExternalClient/ExternalClient';
import ReportUserModal from 'components/ReportUserModal/ReportUserModal';

import { getClientActivityStatus } from 'helpers/clientActivityStatus';
import { parseBriefSpec } from 'helpers/briefSpec';
import briefShape from 'propTypes/briefShape';
import styles from './BriefDetail.module.scss';
import {
  FREE_USER_JOB_PITCH_LIMIT,
  VERY_POPULAR_PITCH_COUNT,
} from 'constants/constants';
import SignUp from 'components/SignUp/SignUp';

// Clients sometimes flag jobs inside the title, e.g. "[Urgent] Logo design".
// Those flags are shown as badges next to the title rather than as raw text.
const parseTitleFlags = (title = '') => {
  const flags = [];
  const text = title
    .replace(/\[([^[\]]+)\]/g, (match, flag) => {
      const label = flag.trim();
      if (label) {
        flags.push(label);
      }
      return '';
    })
    .replace(/\s{2,}/g, ' ')
    .trim();

  return { text: text || title, flags };
};

// Applications are only worth advertising once a job has some traction.
const MIN_PITCHES_FOR_APPLICATIONS_CHIP = 5;

const HoverTooltip = ({ id, text, placement = 'bottom', children }) => (
  <OverlayTrigger
    placement={placement}
    overlay={
      <Tooltip id={id} className={styles.tooltip}>
        {text}
      </Tooltip>
    }
  >
    {children}
  </OverlayTrigger>
);

HoverTooltip.propTypes = {
  children: PropTypes.element.isRequired,
  id: PropTypes.string.isRequired,
  placement: PropTypes.string,
  text: PropTypes.node.isRequired,
};

const Chip = ({ icon, children, title }) => (
  <span className={styles.chip} title={title}>
    <SvgIcon className={styles.chipIcon} icon={icon} size="15" />
    {children}
  </span>
);

Chip.propTypes = {
  children: PropTypes.node,
  icon: PropTypes.string.isRequired,
  title: PropTypes.string,
};

// The client's location and sector share a single line, so a long sector
// description is clipped rather than allowed to push the location out of view.
const CLIENT_DESCRIPTION_MAX_LENGTH = 40;

const truncate = (text, maxLength) =>
  text.length > maxLength ? `${text.slice(0, maxLength).trimEnd()}…` : text;

const getInitials = (name = '') => {
  const words = name.trim().split(/\s+/).filter(Boolean);

  if (!words.length) {
    return '?';
  }

  if (words.length > 1) {
    return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
  }

  return words[0].slice(0, 2).toUpperCase();
};

// Clients without a public profile are only placed to a country, never a city.
const getClientLocation = (owner, isPrivate) => {
  const { city, country, text } = owner.location || {};

  if (country) {
    return city && !isPrivate ? `${city}, ${country}` : country;
  }

  return text && text !== 'Private' && !isPrivate ? text : '';
};

const getClientDescription = (owner) => {
  if (!owner.organisation) {
    return '';
  }

  if (!owner.sector || owner.sector.toLowerCase() === 'other') {
    return 'Organization';
  }

  return `${capitalizeWords(
    aOrAn(owner.sector)
  )} ${owner.sector.toLowerCase()} company`;
};

// WhatsApp/SMS and calls share the phone icon, so at most two icons are needed
// to cover every combination of channels in the space of a single short line.
const CONTACT_METHODS = [
  { key: 'email', label: 'email', icon: 'mail' },
  { key: 'sms', label: 'WhatsApp / SMS', icon: 'phone' },
  { key: 'phone', label: 'phone', icon: 'phone' },
];

// Freelancers only receive the details themselves after applying, so the card
// advertises which channels the client accepts.
const getClientContact = (owner) => {
  const options = owner.contact_options || {};
  const methods = CONTACT_METHODS.filter((method) => options[method.key]);

  if (!methods.length) {
    return {
      icons: ['lock'],
      label: 'Contact via Twine',
      title: 'This client keeps their contact details private',
    };
  }

  return {
    icons: methods
      .map((method) => method.icon)
      .filter((icon, index, icons) => icons.indexOf(icon) === index),
    label: 'Contact:',
    labelFirst: true,
    title: `This client accepts contact by ${methods
      .map((method) => method.label)
      .join(', ')}. Twine Pro members get these details after applying.`,
  };
};

const ClientCard = ({ activityStatus, isPrivate, showContact, owner }) => {
  const fullDescription = isPrivate
    ? 'Individual'
    : getClientDescription(owner);
  const description = truncate(fullDescription, CLIENT_DESCRIPTION_MAX_LENGTH);
  const location = getClientLocation(owner, isPrivate);
  const detailsTitle = [location, fullDescription].filter(Boolean).join(' · ');
  const contact = showContact ? getClientContact(owner) : null;

  // Only freelancers have a public portfolio; private clients stay anonymous.
  const profileLink =
    !isPrivate && owner.type === 'seller'
      ? (owner.links && (owner.links.main_relative || owner.links.main)) || ''
      : '';

  const avatar =
    !isPrivate && hasAvatar(owner) ? (
      <Thumbnail
        thumbnails={owner.avatars}
        circle
        className={styles.clientAvatar}
      />
    ) : (
      <div
        className={`${styles.clientAvatar} ${
          isPrivate ? styles.clientAvatarPrivate : styles.clientAvatarInitials
        }`}
      >
        {isPrivate ? (
          <SvgIcon icon="user" width="13" height="18" />
        ) : (
          getInitials(owner.displayname)
        )}
      </div>
    );

  const name = (
    <>
      <span className={styles.clientName}>
        {isPrivate ? 'Private client' : owner.displayname}
      </span>
      {!isPrivate && <UserStatusBadges user={owner} showPro={false} />}
    </>
  );

  return (
    <div className={styles.clientCard}>
      {profileLink ? (
        <Link
          to={profileLink}
          className={styles.clientAvatarLink}
          title={`View ${owner.displayname}'s portfolio`}
        >
          {avatar}
        </Link>
      ) : (
        avatar
      )}
      <div className={styles.clientMain}>
        <div className={styles.clientNameRow}>
          {profileLink ? (
            <Link
              to={profileLink}
              className={styles.clientNameLink}
              title={`View ${owner.displayname}'s portfolio`}
            >
              {name}
            </Link>
          ) : (
            name
          )}
          {owner.is_external && <ExternalClient />}
        </div>
        {(location || description) && (
          <HoverTooltip id="client-details" text={detailsTitle}>
            <div className={styles.clientDetails}>
              {location && (
                <span className={styles.clientLocation}>{location}</span>
              )}
              {description && (
                <span className={styles.clientDescription}>
                  {location ? ` · ${description}` : description}
                </span>
              )}
            </div>
          </HoverTooltip>
        )}
      </div>
      {(activityStatus || contact) && (
        <div className={styles.clientStatus}>
          {activityStatus && (
            <div className={styles.clientActivity}>
              <span className={styles.clientActivityDot} />
              {activityStatus === 'actively_reviewing'
                ? 'Actively reviewing'
                : 'Recently active'}
            </div>
          )}
          {contact && (
            <HoverTooltip id="client-contact" text={contact.title}>
              <div className={styles.clientContact}>
                {contact.labelFirst && contact.label}
                {contact.icons.map((icon) => (
                  <SvgIcon
                    key={icon}
                    className={styles.clientContactIcon}
                    icon={icon}
                    size="12"
                  />
                ))}
                {!contact.labelFirst && contact.label}
              </div>
            </HoverTooltip>
          )}
        </div>
      )}
    </div>
  );
};

ClientCard.propTypes = {
  activityStatus: PropTypes.string,
  isPrivate: PropTypes.bool,
  showContact: PropTypes.bool,
  owner: PropTypes.object.isRequired,
};

class BriefDetail extends React.Component {
  static propTypes = {
    brief: briefShape,
    owner: PropTypes.shape({
      avatars: PropTypes.object,
      contact_options: PropTypes.object,
      displayname: PropTypes.string,
      last_active: PropTypes.string,
      links: PropTypes.shape({
        main: PropTypes.string,
        main_relative: PropTypes.string,
      }),
      location: PropTypes.shape({
        city: PropTypes.string,
        country: PropTypes.string,
        text: PropTypes.string,
      }),
      verified: PropTypes.bool,
      type: PropTypes.oneOf(['seller', 'buyer']),
      user: PropTypes.object,
      organisation: PropTypes.bool,
      sector: PropTypes.string,
      is_external: PropTypes.bool,
    }),
    isExpired: PropTypes.bool,
    isExternal: PropTypes.bool,
    user: PropTypes.object,
    onBookmarkClick: PropTypes.func,
    isSaved: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.state = {
      showReportModal: false,
      showSignUpModal: false,
    };
  }

  handleShowReportModal = () => {
    this.setState({ showReportModal: true });
  };

  componentDidMount() {
    if (window.location.href.includes('report=1')) {
      this.setState({ showReportModal: true });
    }
  }

  render() {
    const { brief, owner, isExpired, isExternal, isSaved, onBookmarkClick } =
      this.props;

    let isPrivate = false;
    if (
      owner.displayname.match(/user[0-9]+/i) ||
      owner.displayname.toLowerCase() === 'client'
    ) {
      isPrivate = true;
    }

    let city = '';
    let country = '';
    let locationLabel = '';
    if (!brief.remote) {
      const locationArr = brief.location.split(', ');
      city = locationArr[0];
      country = locationArr[1];
      // Jobs open to a whole country have no city, e.g. "Germany".
      locationLabel = country ? `${city}, ${country}` : `${city} only`;
    }

    const { text: briefTitle, flags: briefTitleFlags } = parseTitleFlags(
      brief.text
    );
    const isClosed = isExpired || !brief.active;
    const role = brief.role || brief.skill;
    const {
      description: specDescription,
      aboutClient,
      budget,
      details,
    } = parseBriefSpec(brief.spec);
    // The client's "Budget range" answer is the source of truth; only jobs that
    // predate that question (budget === null) fall back to the stored amount.
    const hasBudget = brief.amount && brief.amount !== 0 && brief.amount !== -1;
    let budgetChip;
    if (budget === null) {
      budgetChip = hasBudget ? (
        <Price currency={brief.currency} amount={brief.budget} />
      ) : (
        'Open to offers'
      );
    } else {
      budgetChip = budget || 'Open to offers';
    }
    const showApplications =
      brief.num_of_pitches >= MIN_PITCHES_FOR_APPLICATIONS_CHIP && !isExpired;

    let status;
    if (isClosed) {
      status = {
        label: 'Job closed',
        className: styles.statusClosed,
      };
    } else if (brief.boosted_expires_at) {
      status = {
        label: 'Priority job',
        className: styles.statusPriority,
        icon: 'bolt',
        title: 'This client urgently wants to hire',
      };
    } else if (brief.num_of_pitches >= FREE_USER_JOB_PITCH_LIMIT) {
      status = {
        label:
          brief.num_of_pitches >= VERY_POPULAR_PITCH_COUNT
            ? 'Very popular job'
            : 'Popular job',
        className: styles.statusPopular,
        title: 'Pitch soon – this client has received pitches',
      };
    } else {
      status = {
        label: 'Open job',
        className: styles.statusOpen,
        title: 'Pitch before other freelancers',
      };
    }

    return (
      <div itemScope itemType="https://schema.org/JobPosting">
        <div
          className="hide"
          itemProp="hiringOrganization"
          itemScope
          itemType="https://schema.org/Organization"
        >
          <span itemProp="name" content="Twine" />
          <span itemProp="sameAs" content="https://www.twine.net" />
          <span
            itemProp="logo"
            content="https://www.twine.net/assets/img/logos/twine-social-icon-min.png"
          />
        </div>
        {brief.role ? (
          <div itemProp="title" content={`Freelance ${brief.role}`} />
        ) : (
          <div
            itemProp="title"
            content={`Freelancer for ${brief.skill} Project`}
          />
        )}
        <div itemProp="datePosted" content={brief.approved_at} />
        <div itemProp="validThrough" content={brief.expires_at} />
        <div itemProp="employmentType" content="CONTRACTOR" />
        {brief.remote ? (
          <div>
            <div itemProp="jobLocationType" content="TELECOMMUTE" />
            <div
              itemProp="applicantLocationRequirements"
              itemScope
              itemType="https://schema.org/Country"
            >
              <span itemProp="name" content="USA" />
              <span itemProp="name" content="United Kingdom" />
              <span itemProp="name" content="Canada" />
              <span itemProp="name" content="Australia" />
              <span itemProp="name" content="Austria" />
              <span itemProp="name" content="Belarus" />
              <span itemProp="name" content="Belgium" />
              <span itemProp="name" content="Denmark" />
              <span itemProp="name" content="France" />
              <span itemProp="name" content="Germany" />
              <span itemProp="name" content="Greece" />
              <span itemProp="name" content="Italy" />
              <span itemProp="name" content="Netherlands" />
              <span itemProp="name" content="Portugal" />
              <span itemProp="name" content="Russia" />
              <span itemProp="name" content="Spain" />
              <span itemProp="name" content="Hong Kong" />
              <span itemProp="name" content="Malaysia" />
              <span itemProp="name" content="Singapore" />
              <span itemProp="name" content="South Korea" />
              <span itemProp="name" content="United Arab Emirates" />
            </div>
          </div>
        ) : (
          <div
            itemProp="jobLocation"
            itemScope
            itemType="https://schema.org/Place"
          >
            <span
              itemProp="address"
              itemScope
              itemType="https://schema.org/PostalAddress"
            >
              <span itemProp="addressLocality" content={city} />
              <span itemProp="addressCountry" content={country} />
            </span>
          </div>
        )}
        <Panel className={styles.container} noPadding={true}>
          <Panel.Item>
            <div className={styles.briefHeader}>
              <div className={styles.metaRow}>
                <div className={styles.metaStatus}>
                  <span
                    className={`${styles.statusPill} ${status.className}`}
                    title={status.title}
                  >
                    {status.icon ? (
                      <SvgIcon
                        className={styles.statusIcon}
                        icon={status.icon}
                        size="12"
                      />
                    ) : (
                      <span className={styles.statusDot} />
                    )}
                    {status.label}
                  </span>
                  <span className={styles.metaTime}>
                    {brief.approved_at && (
                      <HoverTooltip
                        id="brief-posted-date"
                        text={`Job posted: ${formatLocalDate(
                          brief.approved_at
                        )}`}
                      >
                        <span>
                          Posted {moment(brief.approved_at).fromNow()}
                        </span>
                      </HoverTooltip>
                    )}
                    {!isClosed && brief.expires_at && (
                      <HoverTooltip
                        id="brief-closing-date"
                        text={`Closing date: ${formatLocalDate(
                          brief.expires_at
                        )}`}
                      >
                        <span>
                          {` · Closes in ${moment(brief.expires_at).fromNow(
                            true
                          )}`}
                        </span>
                      </HoverTooltip>
                    )}
                  </span>
                </div>
                <button
                  type="button"
                  aria-pressed={isSaved}
                  className={`${styles.saveButton} ${
                    isSaved ? styles.saveButtonSaved : ''
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!this.props.user) {
                      this.setState({ showSignUpModal: true });
                    } else {
                      onBookmarkClick();
                    }
                  }}
                >
                  <SvgIcon
                    className={styles.saveIcon}
                    icon={isSaved ? 'bookmark' : 'bookmark-outline'}
                    width="11"
                    height="15"
                  />
                  {isSaved ? 'Saved' : 'Save'}
                </button>
              </div>
              <h1 className={styles.briefTitle} title={briefTitle}>
                {briefTitle}
                {briefTitleFlags.map((flag, index) => (
                  <span
                    key={`${flag}-${index}`}
                    className={`${styles.titleFlag} ${
                      flag.toLowerCase() === 'urgent'
                        ? styles.titleFlagUrgent
                        : ''
                    }`}
                  >
                    {flag}
                  </span>
                ))}
              </h1>
              {role && <div className={styles.briefSubtitle}>{role}</div>}
            </div>
            <div className={styles.chipRow}>
              <Chip icon="coin">{budgetChip}</Chip>
              {brief.remote ? (
                <Chip icon="sphere" title="Remote job">
                  Remote
                </Chip>
              ) : (
                <Chip
                  icon="map-pin"
                  title={`${brief.text} – ${brief.location}`}
                >
                  {locationLabel}
                </Chip>
              )}
              {showApplications && (
                <Chip icon="users">{`${brief.num_of_pitches} applied`}</Chip>
              )}
            </div>
          </Panel.Item>
          <Panel.Item className={styles.clientInfo}>
            <ClientCard
              activityStatus={getClientActivityStatus(brief, owner)}
              isPrivate={isPrivate}
              showContact={!isExternal}
              owner={owner}
            />
          </Panel.Item>
          {brief.spec !== '' ? (
            <Panel.Item>
              <div itemProp="description" className={styles.briefDescription}>
                <Markdown
                  text={
                    (!brief.remote
                      ? `${brief.role || brief.skill} is needed in ${
                          brief.location
                        }.\n\n`
                      : '') + specDescription
                  }
                />
                {aboutClient && (
                  <Markdown text={`**About the client**\n${aboutClient}`} />
                )}
                {details.length > 0 && (
                  <dl className={styles.detailsGrid}>
                    {details.map(({ label, value }) => (
                      <div className={styles.detailsCell} key={label}>
                        <dt className={styles.detailsLabel}>{label}</dt>
                        <dd className={styles.detailsValue}>{value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                <div className={styles.briefFooter}>
                  {this.props.user && (
                    <div className={styles.report}>
                      <Button
                        className="ml"
                        block
                        bsStyle="info"
                        bsSize="sm"
                        onClick={() => {
                          this.handleShowReportModal();
                        }}
                      >
                        <SvgIcon icon="flag" size="14" /> Report user
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Panel.Item>
          ) : null}
          <Modal
            show={this.state.showReportModal}
            closebutton
            onExit={() => {
              this.setState({ showReportModal: false });
            }}
          >
            <ReportUserModal
              accusedUser={this.props.owner}
              claimant={this.props.user}
              isBrief={true}
              onClose={() => this.setState({ showReportModal: false })}
              brief={this.props.brief}
            />
          </Modal>
          {!this.props.user && (
            <Modal
              show={this.state.showSignUpModal}
              subtitle="Sign-up now to get jobs straight to your inbox"
              closeButton
              onExit={() => {
                this.setState({
                  showSignUpModal: false,
                });
              }}
            >
              <SignUp
                redirect={'onboarding'}
                redirectFromGoogle={'signup'}
                defaultAccountType={'seller'}
              />
            </Modal>
          )}
        </Panel>
      </div>
    );
  }
}

export default BriefDetail;
